import { Box, TextInput, UnstyledButton } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import {
	AnalyticsEvent,
	AnalyticsEventType,
	AnalyticsJourney,
	AnalyticsStage,
	analyticsSdk
} from '@newo/analytics';
import { CircleAlert, X } from 'lucide-react';
import { type FC, useCallback, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
	type StepperOnboardingFormValues,
	StepperSetupStepKey
} from '@/stepper/types.ts';
import { cn } from '@/utils';
import { STEP_CONTENT_CLASS_NAMES } from '../../classNames';

type StepperTransfersTextFieldName = Extract<
	keyof StepperOnboardingFormValues,
	'managerEmail' | 'managerFullName'
>;

interface StepperTransfersTextFieldContract {
	analyticsProperty: string;
	testIdPrefix: string;
}

const FIELD_CONTRACTS: Record<
	StepperTransfersTextFieldName,
	StepperTransfersTextFieldContract
> = {
	managerEmail: {
		analyticsProperty: 'manager_email',
		testIdPrefix: 'stepper-manager-email'
	},
	managerFullName: {
		analyticsProperty: 'manager_full_name',
		testIdPrefix: 'stepper-manager-full-name'
	}
};

interface StepperTransfersTextFieldProps {
	clearLabel: string;
	isLocked: boolean;
	label: string;
	name: StepperTransfersTextFieldName;
	placeholder: string;
}

const StepperTransfersTextField: FC<StepperTransfersTextFieldProps> = ({
	clearLabel,
	isLocked,
	label,
	name,
	placeholder
}) => {
	const { analyticsProperty, testIdPrefix } = FIELD_CONTRACTS[name];
	const { control, setValue } = useFormContext<StepperOnboardingFormValues>();
	const value = useWatch({ control, name });
	const previousValueRef = useRef(value);

	const debouncedChange = useDebouncedCallback((nextValue: string) => {
		if (!nextValue.trim()) {
			return;
		}

		analyticsSdk.track(AnalyticsEvent.FieldChanged, {
			field: name,
			journey: AnalyticsJourney.CreatorSetup,
			[analyticsProperty]: nextValue.trim(),
			stage: AnalyticsStage.CreatorOnboarding,
			step: StepperSetupStepKey.CallSummariesTransfers,
			type: AnalyticsEventType.Field
		});
	}, 600);

	const handleChange = useCallback(
		(nextValue: string) => {
			const previousValue = previousValueRef.current.trim();
			previousValueRef.current = nextValue;

			if (!nextValue.trim() && previousValue) {
				analyticsSdk.track(AnalyticsEvent.FieldCleared, {
					field: name,
					journey: AnalyticsJourney.CreatorSetup,
					[`previous_${analyticsProperty}`]: previousValue,
					stage: AnalyticsStage.CreatorOnboarding,
					step: StepperSetupStepKey.CallSummariesTransfers,
					type: AnalyticsEventType.Field
				});
			}

			debouncedChange(nextValue);
		},
		[analyticsProperty, debouncedChange, name]
	);

	const handleFocus = useCallback(() => {
		analyticsSdk.track(AnalyticsEvent.FieldFocused, {
			field: name,
			journey: AnalyticsJourney.CreatorSetup,
			stage: AnalyticsStage.CreatorOnboarding,
			step: StepperSetupStepKey.CallSummariesTransfers,
			type: AnalyticsEventType.Field
		});
	}, [name]);

	const handleClear = () => {
		if (isLocked) {
			return;
		}

		handleChange('');
		setValue(name, '', {
			shouldDirty: true,
			shouldTouch: true,
			shouldValidate: true
		});
	};

	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState }) => (
				<TextInput
					{...field}
					classNames={{
						error: 'mt-1 text-sm text-[#D92D20]',
						input: cn(STEP_CONTENT_CLASS_NAMES.input, {
							'border-[#F04438] focus:border-[#F04438]': fieldState.invalid
						}),
						label: STEP_CONTENT_CLASS_NAMES.formLabel,
						root: 'w-full'
					}}
					data-testid={`${testIdPrefix}-input`}
					error={
						fieldState.error ? (
							<Box
								className="flex items-center gap-1"
								component="span"
							>
								<CircleAlert
									aria-hidden="true"
									className="h-3 w-3"
								/>
								{fieldState.error.message}
							</Box>
						) : undefined
					}
					label={label}
					onChange={(event) => {
						handleChange(event.currentTarget.value);
						field.onChange(event);
					}}
					onFocus={handleFocus}
					placeholder={placeholder}
					readOnly={isLocked}
					rightSection={
						value.trim() && !isLocked ? (
							<UnstyledButton
								aria-label={clearLabel}
								className="inline-flex h-5 w-5 items-center justify-center"
								data-testid={`${testIdPrefix}-clear-button`}
								onClick={handleClear}
								type="button"
							>
								<X
									aria-hidden="true"
									className="h-3 w-3 text-[var(--color-primary-900)]"
								/>
							</UnstyledButton>
						) : undefined
					}
				/>
			)}
		/>
	);
};

export default StepperTransfersTextField;
